import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import * as Icons from "lucide-react";

function Icon({ name, ...p }) { const C = Icons[name] || Icons.Circle; return <C {...p} />; }

const PILLARS = [
  {
    key: "community",
    label: "Community",
    tagline: "Learn, share and grow with fellow MFDs",
    icon: "Users",
    path: "/app/community",
    accent: "blue",
    ring: "from-blue-500/25 to-cyan-500/10",
    iconBg: "bg-blue-500/15 text-blue-400 border-blue-500/25",
    cta: "Enter Community",
    inside: ["Feed & discussions", "Direct messages", "Events & meetups", "AMCs · Consultants · Studios"],
  },
  {
    key: "business",
    label: "Managing Business",
    tagline: "Run your practice — clients, growth and money",
    icon: "Briefcase",
    path: "/app/dashboard",
    accent: "amber",
    ring: "from-amber-500/25 to-orange-500/10",
    iconBg: "bg-amber-500/15 text-amber-400 border-amber-500/25",
    cta: "Open Business",
    inside: ["Dashboard & KPIs", "Clients · Leads · CRM", "Marketing & Content Studio", "Tasks · Wallet · Bookings"],
  },
  {
    key: "learning",
    label: "Learning",
    tagline: "Sharpen skills and stay ahead of the market",
    icon: "GraduationCap",
    path: "/app/learn",
    accent: "violet",
    ring: "from-violet-500/25 to-fuchsia-500/10",
    iconBg: "bg-violet-500/15 text-violet-400 border-violet-500/25",
    cta: "Start Learning",
    inside: ["Academy & NISM prep", "Research Notebook", "Market & Fund intelligence", "Regulatory hub"],
  },
];

export default function Home() {
  const { user } = useAuth();
  const nav = useNavigate();
  const first = (user?.name || "there").split(" ")[0];

  return (
    <div className="max-w-6xl mx-auto" data-testid="home-hub">
      {/* Greeting */}
      <div className="mb-8 lg:mb-10">
        <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-amber-400/80 mb-3">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" /> Your MFDX home
        </div>
        <h1 className="font-head font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-100">
          Welcome back, <span className="text-amber-400">{first}</span>.
        </h1>
        <p className="mt-3 text-slate-400 text-base max-w-xl">
          Pick where you want to go. Everything that runs your MFD practice lives in one of these three spaces.
        </p>
      </div>

      {/* Three pillars */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {PILLARS.map((p) => (
          <button
            key={p.key}
            data-testid={`home-pillar-${p.key}`}
            onClick={() => nav(p.path)}
            className="group relative text-left rounded-2xl border border-[#222938] bg-[#0E1117] p-6 overflow-hidden transition-all duration-300 hover:border-[#2E384D] hover:-translate-y-1 hover:shadow-2xl"
          >
            <div className={`absolute -top-16 -right-16 w-40 h-40 rounded-full bg-gradient-to-br ${p.ring} blur-2xl opacity-60 group-hover:opacity-100 transition-opacity`} />
            <div className="relative">
              <div className={`w-14 h-14 rounded-2xl grid place-items-center border ${p.iconBg} mb-5`}>
                <Icon name={p.icon} size={26} />
              </div>
              <h2 className="font-head font-bold text-xl text-slate-100">{p.label}</h2>
              <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{p.tagline}</p>

              <ul className="mt-5 space-y-2">
                {p.inside.map((it) => (
                  <li key={it} className="flex items-center gap-2 text-[13px] text-slate-400">
                    <Icon name="Check" size={14} className="text-slate-600 shrink-0" /> {it}
                  </li>
                ))}
              </ul>

              <div className="mt-6 flex items-center gap-1.5 text-sm font-semibold text-slate-200 group-hover:text-amber-400 transition-colors">
                {p.cta}
                <Icon name="ArrowRight" size={16} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Quick shortcuts */}
      <div className="mt-8 flex flex-wrap gap-3">
        {[
          { label: "Ask AI Copilot", icon: "Sparkles", path: "/app/ai" },
          { label: "Create Post", icon: "Send", path: "/app/publish" },
          { label: "Today's tasks", icon: "ListChecks", path: "/app/tasks" },
          { label: "Weekly brief", icon: "CalendarCheck", path: "/app/weekly" },
        ].map((s) => (
          <button
            key={s.label}
            data-testid={`home-quick-${s.icon.toLowerCase()}`}
            onClick={() => nav(s.path)}
            className="flex items-center gap-2 rounded-full border border-[#222938] bg-[#141822] hover:bg-[#1A1F2B] hover:border-[#2E384D] px-4 py-2 text-sm font-medium text-slate-300 transition-colors"
          >
            <Icon name={s.icon} size={15} className="text-amber-400" /> {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}
